'use memo'

// web/src/features/event/components/EventModal.tsx
import { useSelector } from 'src/hooks/useRedux'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { EventFormProvider } from '../EventContext'
import EventModalContent from './EventModalContent'

interface EventModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Fired only after a save succeeds, before the modal closes. Separate from
   * `onClose` so a cancel does not trigger the caller's refresh.
   */
  onSaved?: (saved: SnEvent) => void
  /** Null for add mode; the event being edited otherwise. */
  event: SnEvent | null
  /** Absent for a schedule-owned event, which belongs to no team. */
  teamId?: string
  /** Required to create a team event; not needed when only editing/deleting (event detail page). */
  rosterId?: string
  /**
   * Set instead of `teamId`/`rosterId` to create an event owned by a schedule
   * (a competition, season or division) rather than by a team. The competition
   * schedule page has no team of its own, so this is how it creates events.
   */
  scheduleId?: string
}

/**
 * Add/edit event modal. One component serves both modes — the mode is derived
 * from whether `event` is provided.
 */
const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  event,
  teamId,
  rosterId,
  scheduleId,
}) => {
  // The authoring admin's OWN account, already in redux from login — so no
  // fetch, and no permission risk from reading another entity's account.
  // Times typed in this form are interpreted in this zone; viewers see the
  // resulting instant in their own browser zone.
  const accountTimezone = useSelector(
    (state: any) => state.account?._meAccount?.address?.timezone
  )
  const defaultTimezone =
    accountTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  if (!isOpen) {
    return null
  }

  return (
    <EventFormProvider
      event={event}
      teamId={teamId}
      rosterId={rosterId}
      scheduleId={scheduleId}
      defaultTimezone={defaultTimezone}
      onSaved={(saved) => {
        onSaved?.(saved)
        onClose()
      }}
    >
      <EventModalContent
        isOpen={isOpen}
        onClose={onClose}
        event={event}
        scheduleId={scheduleId}
      />
    </EventFormProvider>
  )
}

export default EventModal
